import { useState } from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { ChevronRight, Images, List, Map as MapIcon, MapPin, Waves, X } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { MapCanvas, type MapFeatureProperties } from "@/components/MapCanvas"
import { captureAnalyticsEvent } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"
import {
  localePathsFrom,
  publicMetaDescription,
  type AlternateLink,
  type PublicLocale,
} from "@/lib/public-links"

type Destination = {
  id: number
  name: string
  slug: string
  latitude: number | null
  longitude: number | null
  path: string
}

type Category = {
  id: number
  key: string
  label: string
  slug: string
  intro: string
  meta_title: string | null
  meta_description: string | null
  icon: string
  path: string
}

type Place = {
  id: number
  name: string
  summary: string
  path: string
  destination_name: string
  chips: string[]
  photo: {
    id: number
    alt_text: string
    credit: string | null
    card_url: string
  } | null
}

type Filter = {
  key: string
  label: string
  active: boolean
  href: string
}

const COPY = {
  en: {
    home: "Home",
    titleJoiner: "on",
    results: "published places",
    result: "published place",
    filters: "Filter places",
    clear: "Clear filters",
    emptyTitle: "No places match these filters",
    emptyText: "Try removing one or more filters to see the published places in this category.",
    emptyUnfilteredTitle: "No published places yet",
    emptyUnfilteredText: "There are no published places in this category yet.",
    photoPending: "Photo being prepared",
    openPlace: "View details",
    view: "View",
    list: "List",
    map: "Map",
    closeMapPreview: "Close beach preview",
    noFacts: "Open the beach page for practical details.",
  },
  de: {
    home: "Startseite",
    titleJoiner: "auf",
    results: "veröffentlichte Orte",
    result: "veröffentlichter Ort",
    filters: "Orte filtern",
    clear: "Filter zurücksetzen",
    emptyTitle: "Keine Orte passen zu diesen Filtern",
    emptyText: "Entferne einen oder mehrere Filter, um veröffentlichte Orte in dieser Kategorie zu sehen.",
    emptyUnfilteredTitle: "Noch keine veröffentlichten Orte",
    emptyUnfilteredText: "In dieser Kategorie sind noch keine Orte veröffentlicht.",
    photoPending: "Foto wird vorbereitet",
    openPlace: "Details ansehen",
    view: "Ansicht",
    list: "Liste",
    map: "Karte",
    closeMapPreview: "Strandvorschau schließen",
    noFacts: "Öffne die Strandseite für praktische Angaben.",
  },
} satisfies Record<PublicLocale, Record<string, string>>

export default function CategoryShow({
  locale,
  destination,
  category,
  places,
  filters,
  filtered,
  initial_view,
  robots,
  canonical_url,
  map_data_url,
  alternate_links,
}: {
  locale: PublicLocale
  destination: Destination
  category: Category
  places: Place[]
  filters: Filter[]
  filtered: boolean
  initial_view: "list" | "map"
  robots: "index,follow" | "noindex,follow"
  canonical_url: string
  map_data_url: string
  alternate_links: AlternateLink[]
}) {
  const copy = COPY[locale]
  const { props } = usePage<PageProps>()
  const [view, setView] = useState<"list" | "map">(initial_view)
  const [selectedPlace, setSelectedPlace] = useState<MapFeatureProperties | null>(null)
  const pageTitle = category.meta_title ?? `${category.label} ${copy.titleJoiner} ${destination.name}`
  const description = publicMetaDescription(
    category.meta_description ?? category.intro,
    locale === "de"
      ? `Entdecke veröffentlichte Orte und praktische Angaben für ${destination.name}.`
      : `Explore published places and practical details for ${destination.name}.`,
  )
  const changeView = (nextView: "list" | "map") => {
    setView(nextView)
    setSelectedPlace(null)

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href)
      url.searchParams.set("view", nextView)
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`)
    }

    captureAnalyticsEvent("category_view_changed", { category: category.key, view: nextView })
  }
  const selectPlace = (place: MapFeatureProperties) => {
    setSelectedPlace(place)
    captureAnalyticsEvent("map_marker_selected", {
      place_id: place.id,
      feature_kind: place.feature_kind,
      source: "category_map",
    })
  }

  return (
    <>
      <Head title={pageTitle}>
        <meta name="description" content={description} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <link rel="canonical" href={canonical_url} />
        <meta name="robots" content={robots} />
        {alternate_links.map((link) => (
          <link key={link.href_lang} rel="alternate" hrefLang={link.href_lang} href={link.href} />
        ))}
      </Head>

      <PublicShell locale={locale} localePaths={localePathsFrom(alternate_links)}>
        <main className="pb-10">
          <section className="public-container pt-4 md:pt-6">
            <Breadcrumbs locale={locale} destination={destination} category={category} homeLabel={copy.home} />
            <div className="max-w-3xl py-5 md:py-8">
              <p className="public-kicker">{destination.name}</p>
              <h1 className="public-page-title mt-2">
                {category.label} {copy.titleJoiner} {destination.name}
              </h1>
              <p className="public-lede mt-3">{category.intro}</p>
            </div>
          </section>

          {filters.length > 0 && (
            <section className="border-y border-hairline bg-surface">
              <div className="public-container py-4">
                <div className="flex items-baseline justify-between gap-4">
                  <h2 className="public-card-title">{copy.filters}</h2>
                  {filtered && <Link href={pathWithView(category.path, view)} className="text-sm font-semibold">{copy.clear}</Link>}
                </div>
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label={copy.filters}>
                  {filters.map((filter) => (
                    <Link
                      key={filter.key}
                      href={pathWithView(filter.href, view)}
                      preserveScroll
                      onClick={() => captureAnalyticsEvent("category_filter_toggled", {
                        category: category.key,
                        filter: filter.key,
                        active_after_click: !filter.active,
                      })}
                      aria-pressed={filter.active}
                      className={filter.active
                        ? "shrink-0 rounded-full border border-accent bg-accent px-3.5 py-2 text-sm font-semibold text-white no-underline"
                        : "shrink-0 rounded-full border border-hairline-strong bg-surface px-3.5 py-2 text-sm font-semibold text-ink-secondary no-underline hover:border-accent hover:text-accent"}
                    >
                      {filter.label}
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="public-container py-6 md:py-8">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-ink-muted">
                <strong className="text-ink-display">{places.length}</strong>{" "}
                {places.length === 1 ? copy.result : copy.results}
              </p>
              <div className="flex rounded-lg border border-hairline bg-surface p-1" aria-label={copy.view}>
                <button type="button" aria-pressed={view === "list"} onClick={() => changeView("list")} className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-semibold ${view === "list" ? "bg-accent-hover text-surface" : "text-ink-muted"}`}><List className="h-4 w-4" aria-hidden />{copy.list}</button>
                <button type="button" aria-pressed={view === "map"} onClick={() => changeView("map")} className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-semibold ${view === "map" ? "bg-accent-hover text-surface" : "text-ink-muted"}`}><MapIcon className="h-4 w-4" aria-hidden />{copy.map}</button>
              </div>
            </div>

            {view === "map" && destination.longitude !== null && destination.latitude !== null ? (
              <div className="relative mt-4">
                <MapCanvas
                  maptilerKey={props.maptiler_key}
                  center={[ destination.longitude, destination.latitude ]}
                  dataUrl={map_data_url}
                  onFeatureSelect={selectPlace}
                  className="h-[32rem]"
                />
                {selectedPlace && (
                  <MapPlacePreview
                    place={selectedPlace}
                    copy={copy}
                    onClose={() => setSelectedPlace(null)}
                  />
                )}
              </div>
            ) : places.length > 0 ? (
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {places.map((place) => (
                  <PlaceCard key={place.id} place={place} copy={copy} />
                ))}
              </div>
            ) : (
              <div className="public-muted-card mt-4 p-6 text-center md:p-10">
                <h2 className="public-card-title">
                  {filtered ? copy.emptyTitle : copy.emptyUnfilteredTitle}
                </h2>
                <p className="public-card-description mx-auto mt-2 max-w-lg">
                  {filtered ? copy.emptyText : copy.emptyUnfilteredText}
                </p>
                {filtered && <Link href={pathWithView(category.path, view)} className="mt-4 inline-block text-sm font-semibold">{copy.clear}</Link>}
              </div>
            )}
          </section>
        </main>
      </PublicShell>
    </>
  )
}

function MapPlacePreview({
  place,
  copy,
  onClose,
}: {
  place: MapFeatureProperties
  copy: (typeof COPY)[PublicLocale]
  onClose: () => void
}) {
  const facts: string[] = place.key_facts ? JSON.parse(place.key_facts) : []

  return (
    <article
      aria-live="polite"
      aria-labelledby={`map-place-${place.id}`}
      className="absolute right-3 bottom-20 left-3 z-10 overflow-hidden rounded-xl border border-hairline bg-surface shadow-md sm:right-auto sm:bottom-3 sm:w-[23rem]"
    >
      <button type="button" onClick={onClose} aria-label={copy.closeMapPreview} className="absolute top-2 right-2 z-10 rounded-full bg-surface/90 p-2 text-ink-display shadow-sm">
        <X className="h-4 w-4" aria-hidden />
      </button>
      <div className={place.photo_url ? "grid grid-cols-[5.75rem_1fr]" : undefined}>
        {place.photo_url && (
          <img src={place.photo_url} alt={place.photo_alt ?? ""} className="h-full min-h-36 w-full object-cover" />
        )}
        <div className="min-w-0 p-3 pr-11">
          <p className="public-kicker">{place.category_label}</p>
          <h2 id={`map-place-${place.id}`} className="public-card-title mt-1">{place.name}</h2>
          {place.summary && <p className="public-card-description mt-1 line-clamp-2">{place.summary}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(facts.length > 0 ? facts.slice(0, 2) : [ copy.noFacts ]).map((fact) => (
              <span key={fact} className="public-compact-note rounded-full bg-surface-subtle px-2 py-0.5">{fact}</span>
            ))}
          </div>
          <Link
            href={place.path}
            onClick={() => captureAnalyticsEvent("place_card_clicked", { place_id: place.id, source: "category_map" })}
            className="public-contact-link mt-3 flex items-center gap-1"
          >
            {copy.openPlace}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </article>
  )
}

function pathWithView(path: string, view: "list" | "map") {
  const url = new URL(path, "https://xplorecroatia.local")
  url.searchParams.set("view", view)
  return `${url.pathname}${url.search}${url.hash}`
}

function PlaceCard({
  place,
  copy,
}: {
  place: Place
  copy: (typeof COPY)[PublicLocale]
}) {
  return (
    <Link href={place.path} onClick={() => captureAnalyticsEvent("place_card_clicked", { place_id: place.id, source: "category_listing" })} className="public-card group block no-underline transition-shadow hover:shadow-md">
      {place.photo ? (
        <figure className="relative">
          <img src={place.photo.card_url} alt={place.photo.alt_text} loading="lazy" className="aspect-[16/9] w-full object-cover" />
          {place.photo.credit && <figcaption className="absolute right-2 bottom-2 rounded-full bg-ink-display/75 px-2.5 py-1 text-xs text-surface">{place.photo.credit}</figcaption>}
        </figure>
      ) : (
        <div className="flex aspect-[16/9] items-center justify-center bg-surface-subtle text-ink-muted">
          <div className="text-center">
            <Images className="mx-auto h-6 w-6" aria-hidden />
            <span className="mt-2 block text-xs">{copy.photoPending}</span>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="public-card-title">{place.name}</h2>
            <p className="mt-1 flex items-center gap-1 text-xs text-ink-muted">
              <MapPin className="h-3 w-3" aria-hidden />
              {place.destination_name}
            </p>
          </div>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-faded text-accent">
            <Waves className="h-4 w-4" aria-hidden />
          </span>
        </div>
        <p className="public-card-description mt-3 line-clamp-2">{place.summary}</p>
        {place.chips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {place.chips.map((chip) => (
              <span key={chip} className="rounded-full bg-signal-faded px-2.5 py-1 text-xs font-semibold text-signal">
                {chip}
              </span>
            ))}
          </div>
        )}
        <span className="mt-4 flex items-center justify-between border-t border-surface-subtle pt-3 text-sm font-semibold text-accent">
          {copy.openPlace}
          <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
        </span>
      </div>
    </Link>
  )
}

function Breadcrumbs({
  locale,
  destination,
  category,
  homeLabel,
}: {
  locale: PublicLocale
  destination: Destination
  category: Category
  homeLabel: string
}) {
  return (
    <nav className="flex flex-wrap items-center gap-1.5 text-[0.78rem] text-ink-muted" aria-label="Breadcrumb">
      <Link href={`/${locale}`} className="text-ink-muted no-underline hover:text-ink-display">{homeLabel}</Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <Link href={destination.path} className="text-ink-muted no-underline hover:text-ink-display">{destination.name}</Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <span className="font-semibold text-ink-secondary">{category.label}</span>
    </nav>
  )
}
